import Graphemer from 'graphemer';

// Hebrew -> Paleo map with regular letters only
const hebrewToPaleoMap: Record<string, string> = {
  'א': '𐤀', 'ב': '𐤁', 'ג': '𐤂', 'ד': '𐤃', 'ה': '𐤄', 'ו': '𐤅', 'ז': '𐤆',
  'ח': '𐤇', 'ט': '𐤈', 'י': '𐤉', 'כ': '𐤊', 'ל': '𐤋', 'מ': '𐤌',
  'נ': '𐤍', 'ס': '𐤎', 'ע': '𐤏', 'פ': '𐤐',
  'צ': '𐤑', 'ק': '𐤒', 'ר': '𐤓', 'ש': '𐤔', 'ת': '𐤕',
  // Map final letters to regular letters
  'ך': '𐤊', 'ם': '𐤌', 'ן': '𐤍', 'ף': '𐤐', 'ץ': '𐤑'
};

// Create reverse mapping for Paleo to Square Hebrew
const paleoToHebrewMap: Record<string, string> = Object.entries(hebrewToPaleoMap)
  .reduce((acc, [key, value]) => ({
    ...acc,
    [value]: key
  }), {});

// Hebrew -> English (QWERTY) dict for toLatinQwerty:
// If the user wants a robust mapping for each letter, define here.
// Example from earlier snippet:
const hebrewToEnglishDict: Record<string, string> = {
  '/': 'q', '׳': 'w', 'ק': 'e', 'ר': 'r', 'א': 't', 'ט': 'y', 'ו': 'u', 'ן': 'i',
  'ם': 'o', 'פ': 'p', 'ש': 'a', 'ד': 's', 'ג': 'd', 'כ': 'f', 'ע': 'g', 'י': 'h',
  'ח': 'j', 'ל': 'k', 'ך': 'l', 'ף': ';', 'ז': 'z', 'ס': 'x', 'ב': 'c', 'ה': 'v',
  'נ': 'b', 'מ': 'n', 'צ': 'm', 'ת': ',', ' ': ' ', ',': '<', '.': '>', "'": '"',
  '"': '\'', '(': ')', ')': '(', '\n': '\n',
  'ץ': '.'
};

// Build an English->Hebrew dict if we want typed English to become Hebrew.
function buildEnglishToHebrewDict(): Record<string, string> {
  const base: Record<string, string> = {
    'q': '/', 'w': '׳', 'e': 'ק', 'r': 'ר', 't': 'א', 'y': 'ט', 'u': 'ו', 'i': 'ן',
    'o': 'ם', 'p': 'פ', 'a': 'ש', 's': 'ד', 'd': 'ג', 'f': 'כ', 'g': 'ע', 'h': 'י',
    'j': 'ח', 'k': 'ל', 'l': 'ך', ';': 'ף', 'z': 'ז', 'x': 'ס', 'c': 'ב', 'v': 'ה',
    'b': 'נ', 'n': 'מ', 'm': 'צ', ',': 'ת',
    '.': 'ץ',
    '<': ',', '>': '.', ' ': ' ',
    "'": ',', '"': '"', '(': ')', ')': '(', '\n': '\n',
  };
  const result: Record<string, string> = { ...base };
  // uppercase => same letter in Hebrew?
  Object.keys(base).forEach(k => {
    if (/^[a-z]$/.test(k)) {
      const up = k.toUpperCase();
      result[up] = base[k];
    }
  });
  return result;
}

const englishToHebrewDict = buildEnglishToHebrewDict();

// Punctuation characters preserved when "Keep Punctuation" is on
const preservedPunctuation = new Set(['"', "'", ',', '.']);

// For removing Niqqud:
export function removeNiqqud(s: string) {
  return s.replace(/[\u0591-\u05BD\u05BF\u05C1-\u05C2\u05C4-\u05C5\u05C7]/g, '');
}

// Simple gematria table
const hebrewGematriaMap: Record<string, number> = {
  'א': 1, 'ב': 2, 'ג': 3, 'ד': 4, 'ה': 5,
  'ו': 6, 'ז': 7, 'ח': 8, 'ט': 9, 'י': 10,
  'כ': 20, 'ך': 20, 'ל': 30, 'מ': 40, 'ם': 40,
  'נ': 50, 'ן': 50, 'ס': 60, 'ע': 70, 'פ': 80,
  'ף': 80, 'צ': 90, 'ץ': 90, 'ק': 100, 'ר': 200,
  'ש': 300, 'ת': 400,
};

////////////////////////////////////////////
// Transformation Helpers
////////////////////////////////////////////

// Hebrew -> English (QWERTY)
export function toLatinQwerty(text: string, keepPunctuation: boolean = false): string {
  const stripped = removeNiqqud(text);
  return stripped
    .split('')
    .map(ch => {
      if (keepPunctuation && preservedPunctuation.has(ch)) return ch;
      return hebrewToEnglishDict[ch] || ch;
    })
    .join('');
}

// Hebrew -> Paleo Hebrew
export function toPaleoHebrew(text: string): string {
  const stripped = removeNiqqud(text);
  return stripped.split('').map(ch => hebrewToPaleoMap[ch] || ch).join('');
}

// English -> Hebrew (קראטון)
export function toKratonHebrew(text: string, keepPunctuation: boolean = false): string {
  return text.split('').map(ch => {
    if (keepPunctuation && preservedPunctuation.has(ch)) return ch;
    return englishToHebrewDict[ch] || ch;
  }).join('');
}

// Summation gematria
export function toGematria(text: string): string {
  let sum = 0;
  for (const ch of text) {
    if (hebrewGematriaMap[ch]) {
      sum += hebrewGematriaMap[ch];
    }
  }
  return String(sum);
}

// Clean archaeological/epigraphic punctuation from Paleo-Hebrew transcriptions
function cleanArchaeologicalPunctuation(text: string): string {
  let result = text;
  // Remove line numbers at start of lines (e.g. "2.", "13.")
  result = result.replace(/^\d+\.\s*/gm, '');
  // Remove brackets and dashes (damaged/missing text markers)
  result = result.replace(/\[/g, '').replace(/]/g, '');
  result = result.replace(/---/g, '').replace(/--/g, '').replace(/-/g, '');
  // Replace dots with spaces (word dividers in inscriptions)
  result = result.replace(/\./g, ' ');
  // Collapse multiple spaces
  result = result.replace(/[^\S\r\n]{2,}/g, ' ');
  // Trim each line
  result = result.split('\n').map(l => l.trim()).join('\n');
  // Remove empty lines
  result = result.replace(/\n{2,}/g, '\n');
  return result.trim();
}

// Apply final letter forms at word boundaries
function applyFinalLetters(text: string): string {
  const regularToFinal: Record<string, string> = {
    'כ': 'ך', 'מ': 'ם', 'נ': 'ן', 'פ': 'ף', 'צ': 'ץ',
  };
  const chars = Array.from(text);
  for (let i = 0; i < chars.length; i++) {
    if (!(chars[i] in regularToFinal)) continue;
    let next = i + 1;
    while (next < chars.length && /[\u0591-\u05BD\u05BF\u05C1-\u05C2\u05C4-\u05C5\u05C7]/.test(chars[next])) next++;
    const isLastInWord = next === chars.length || !/[\u05D0-\u05EA]/.test(chars[next]);
    if (isLastInWord) {
      chars[i] = regularToFinal[chars[i]];
    }
  }
  return chars.join('');
}

// Add transformation function
export function paleoToSquare(text: string, convertFinals: boolean = false, cleanPunc: boolean = false): string {
  let input = text;
  if (cleanPunc) {
    input = cleanArchaeologicalPunctuation(input);
  }
  let result = Array.from(input)
    .map(char => {
      const regularChar = paleoToHebrewMap[char];
      // Map final letters to regular ones
      if (regularChar === 'ך') return 'כ';
      if (regularChar === 'ם') return 'מ';
      if (regularChar === 'ן') return 'נ';
      if (regularChar === 'ף') return 'פ';
      if (regularChar === 'ץ') return 'צ';
      return regularChar || char;
    })
    .join('');
  if (convertFinals) {
    result = applyFinalLetters(result);
  }
  return result;
}


export function reverseWithNiqqud(text: string): string {
  const segmenter = typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
  return text.split(/(\r\n|\n|\r)/).map((line, index) => {
    if (index % 2) return line;
    const groups = segmenter
      ? Array.from(segmenter.segment(line), item => item.segment)
      : new Graphemer().splitGraphemes(line);
    return groups.reverse().join('');
  }).join('');
}

export const tools = [
  { title: 'Hebrew Keyboard', description: 'Fix text typed with an English keyboard.', input: 'English QWERTY', output: 'Hebrew', inputDir: 'ltr', outputDir: 'rtl', example: 'akuo', sample: 'akuo → שלום' },
  { title: 'English Keyboard', description: 'Fix text typed with a Hebrew keyboard.', input: 'Hebrew keyboard', output: 'English QWERTY', inputDir: 'rtl', outputDir: 'ltr', example: 'שלום', sample: 'שלום → akuo' },
  { title: 'Remove Niqqud', description: 'Remove vowel marks. Keep punctuation.', input: 'Hebrew with niqqud', output: 'Hebrew without niqqud', inputDir: 'rtl', outputDir: 'rtl', example: 'שָׁלוֹם, עוֹלָם!', sample: 'שָׁלוֹם → שלום' },
  { title: 'Square Hebrew', description: 'Convert ancient script to modern Hebrew.', input: 'Paleo-Hebrew', output: 'Modern Hebrew', inputDir: 'rtl', outputDir: 'rtl', example: '𐤔𐤋𐤅𐤌', sample: '𐤔𐤋𐤅𐤌 → שלום' },
  { title: 'Paleo-Hebrew', description: 'Convert Hebrew to ancient script.', input: 'Hebrew', output: 'Paleo-Hebrew', inputDir: 'rtl', outputDir: 'rtl', example: 'שלום', sample: 'שלום → 𐤔𐤋𐤅𐤌' },
  { title: 'Gematria', description: 'Add the values of Hebrew letters.', input: 'Hebrew', output: 'Numerical value', inputDir: 'rtl', outputDir: 'ltr', example: 'שלום', sample: 'שלום → 376' },
  { title: 'Reverse', description: 'Reverse each line. Keep vowel marks attached.', input: 'Any text', output: 'Reversed text', inputDir: 'auto', outputDir: 'auto', example: 'abc\nשָׁלוֹם', sample: 'abc → cba' },
] as const;
export type ToolName = typeof tools[number]['title'];
export interface TransformOptions { keepPunctuation: boolean; finalLetters: boolean; cleanPunctuation: boolean; }
export function transformText(text: string, tool: ToolName, options: TransformOptions): string {
  if (!text) return '';
  switch (tool) {
    case 'Hebrew Keyboard': return toKratonHebrew(text, options.keepPunctuation);
    case 'English Keyboard': return toLatinQwerty(text, options.keepPunctuation);
    case 'Remove Niqqud': return removeNiqqud(text);
    case 'Square Hebrew': return paleoToSquare(text, options.finalLetters, options.cleanPunctuation);
    case 'Paleo-Hebrew': return toPaleoHebrew(text);
    case 'Gematria': return toGematria(text);
    case 'Reverse': return reverseWithNiqqud(text);
  }
}
